"use client";

import { Heart, ShoppingBag, Tag, Zap } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useWishlistStore } from "@/components/providers/wishlist-store";
import { formatPrice } from "@/lib/utils";
import { cn } from "@/lib/utils";
import type { Product, ProductVariant } from "@/types";

type ProductCardProps = {
  product: Product;
  compact?: boolean;
  sponsored?: boolean;
};

export function ProductCard({ product, compact = false, sponsored = false }: ProductCardProps) {
  const toggleWishlist = useWishlistStore((s) => s.toggle);
  const isWishlisted = useWishlistStore((s) => s.has(product.id));
  const [mounted, setMounted] = useState(false);
  const [previewVariant, setPreviewVariant] = useState<ProductVariant | null>(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => setMounted(true), []);

  const safeIsWishlisted = mounted ? isWishlisted : false;

  const variants = (product.variants && product.variants.length > 0 ? product.variants : []).filter(
    (v) => v.isActive !== false
  );

  // Calculate pricing range across base price and variants
  const variantPrices = variants
    .map((v) => Number(v.price))
    .filter((p) => !isNaN(p) && p > 0);
  const allPrices = [product.price, ...variantPrices];
  const minPrice = Math.min(...allPrices);
  const maxPrice = Math.max(...allPrices);
  const hasPriceRange = maxPrice > minPrice;

  // Active price and discount based on previewed variant or base product
  const effectivePrice = previewVariant ? previewVariant.price : product.price;
  const effectiveMrp = previewVariant?.mrp ?? product.mrp;
  const effectiveDiscount =
    effectiveMrp > effectivePrice
      ? Math.round(((effectiveMrp - effectivePrice) / effectiveMrp) * 100)
      : product.discount;
  const savings = effectiveMrp - effectivePrice;
  const isDeal = product.isFlashDeal || effectiveDiscount >= 20;

  const rawImage = previewVariant?.image || product.images[0] || "";
  const hasImage = Boolean(rawImage && rawImage !== "/placeholder.png" && !imgError);

  const productUrl = previewVariant
    ? `/products/${product.slug}?variant=${encodeURIComponent(previewVariant.id)}`
    : `/products/${product.slug}`;

  // Sold count representation matching AliExpress/Amazon style (e.g. 1,000+ sold)
  const soldCount =
    product.reviewCount >= 1000
      ? "1,000+ sold"
      : product.reviewCount > 0
      ? `${product.reviewCount} sold`
      : "100+ sold";

  return (
    <article
      onMouseLeave={() => {
        setPreviewVariant(null);
        setImgError(false);
      }}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white transition-all duration-200 hover:border-[#00B7B0]/60 hover:shadow-md dark:bg-slate-900 border border-slate-200/90 dark:border-white/10 dark:hover:border-[#00B7B0]/40"
    >
      {/* Product Image Area:
          Desktop: 200–240px wide, 200–240px height (aspect-square)
          Mobile: 160–190px wide, 160–190px height (aspect-square)
      */}
      <Link
        href={productUrl}
        className="relative block aspect-square w-full overflow-hidden bg-[#F5F7FA] dark:bg-slate-800/90"
      >
        {hasImage ? (
          <Image
            src={rawImage}
            alt={previewVariant?.title || product.title}
            fill
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 16vw"
            className="object-contain p-2 sm:p-2.5 transition duration-300 group-hover:scale-[1.04]"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <ShoppingBag className="h-10 w-10 sm:h-12 sm:w-12 text-slate-400 dark:text-slate-500 stroke-[1.25]" />
          </div>
        )}

        {/* Brand Tag (e.g. Lenovo on top-left) */}
        {product.brand && product.brand.toLowerCase() === "lenovo" && (
          <div className="absolute left-1.5 top-1.5 z-10 rounded-xs bg-[#073B70] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-tight text-white shadow-xs">
            Lenovo
          </div>
        )}

        {/* Wishlist Button (always accessible on mobile touch screens, hover-revealed on desktop) */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleWishlist(product);
            toast.success(isWishlisted ? "Removed from wishlist" : "Saved to wishlist");
          }}
          className={cn(
            "absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/95 p-1.5 text-slate-500 shadow-sm transition hover:text-[#FF8A00] active:scale-90 dark:bg-slate-800/95 dark:text-slate-300",
            safeIsWishlisted && "!text-[#FF8A00] fill-[#FF8A00] bg-white shadow-md"
          )}
          aria-label={safeIsWishlisted ? "Remove from wishlist" : "Add to wishlist"}
        >
          <Heart className={cn("h-4 w-4 transition-transform", safeIsWishlisted && "fill-current scale-110")} />
        </button>

        {/* Exclusive Deal / Free Delivery Ribbon Banner at bottom-left */}
        {(product.isFlashDeal || effectiveDiscount >= 30) && (
          <div className="absolute bottom-0 left-0 z-10 max-w-[85%] truncate rounded-tr-lg bg-gradient-to-r from-[#FF8A00] to-[#E67C00] px-2 py-0.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-tight text-white shadow-xs">
            EXCLUSIVE DEAL • Free Delivery
          </div>
        )}
      </Link>

      {/* Content Area Below Image:
          Mobile: p-2.5, Desktop: p-3
      */}
      <div className="flex flex-1 flex-col justify-between p-2.5 sm:p-3">
        <div>
          {/* AliExpress/Amazon-style Color Swatches:
              Enlarged touch targets for mobile thumbs (min 20px)
          */}
          {variants.length > 0 ? (
            <div
              className="mb-1.5 flex items-center gap-1.5 flex-wrap min-h-[22px]"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <div className="flex items-center gap-1.5">
                {variants.slice(0, 4).map((v) => {
                  const isSelected = previewVariant?.id === v.id;
                  const label = v.color || v.title || "Color";
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onMouseEnter={() => {
                        setPreviewVariant(v);
                        setImgError(false);
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setPreviewVariant((prev) => (prev?.id === v.id ? null : v));
                        setImgError(false);
                      }}
                      title={`${label}${v.price ? ` - ${formatPrice(v.price)}` : ""}`}
                      className={cn(
                        "relative h-4 w-4 sm:h-3.5 sm:w-3.5 shrink-0 rounded-full border border-slate-300 dark:border-slate-600 shadow-2xs transition-all active:scale-125 focus:outline-none overflow-hidden",
                        isSelected
                          ? "ring-2 ring-[#087DB5] scale-115 z-10 border-white shadow-sm"
                          : "opacity-85 hover:opacity-100"
                      )}
                      aria-label={`Preview ${label}`}
                    >
                      {v.image ? (
                        <img
                          src={v.image}
                          alt={label}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <span
                          className="block h-full w-full"
                          style={{ backgroundColor: v.colorCode || "#111" }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {variants.length > 4 && (
                <span className="text-[10px] text-slate-500 dark:text-slate-400 font-bold leading-none">
                  +{variants.length - 4}
                </span>
              )}

              <span className="truncate text-[10px] text-slate-500 dark:text-slate-400 font-medium leading-none max-w-[90px]">
                {previewVariant
                  ? previewVariant.color || previewVariant.title
                  : `${variants.length} colors`}
              </span>
            </div>
          ) : (
            <div className="mb-1.5 min-h-[22px]" />
          )}

          {/* Product Details Link: Navigates directly to the product detail page */}
          <Link href={productUrl} className="block group/link">
            {/* Product Name: Clamped strictly to 2 lines to maintain uniform card height */}
            <h3
              className="line-clamp-2 min-h-[2.1rem] sm:min-h-[2.4rem] text-xs sm:text-[13px] font-semibold leading-snug text-[#073B70] group-hover/link:text-[#087DB5] dark:text-white dark:group-hover/link:text-[#00B7B0] transition-colors"
              title={product.title}
            >
              {product.title}
            </h3>

            {/* Price Row: High-contrast readable price, MRP strike-through, and orange discount badge */}
            <div className="mt-1.5 flex flex-wrap items-baseline gap-1.5 leading-none">
              <span className="text-[14px] sm:text-base font-bold tracking-tight text-slate-950 dark:text-white">
                {previewVariant ? (
                  formatPrice(previewVariant.price)
                ) : hasPriceRange ? (
                  <span>
                    {formatPrice(minPrice)} <span className="font-normal text-slate-400 dark:text-slate-500">–</span> {formatPrice(maxPrice)}
                  </span>
                ) : (
                  formatPrice(product.price)
                )}
              </span>
              {savings > 0 && (
                <span className="text-[10px] sm:text-xs text-slate-400 line-through">
                  {formatPrice(effectiveMrp)}
                </span>
              )}
              {effectiveDiscount > 0 && (
                <span className="rounded bg-[#FF8A00] px-1.5 py-0.5 text-[9.5px] sm:text-[11px] font-bold text-white shadow-2xs">
                  -{effectiveDiscount}%
                </span>
              )}
            </div>

            {/* Rating Stars & Sold Count */}
            <div className="mt-1.5 flex items-center gap-1 leading-none text-slate-600 dark:text-slate-300">
              {/* 5 mini solid stars */}
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    viewBox="0 0 24 24"
                    className={cn(
                      "h-2.5 w-2.5 sm:h-3 sm:w-3",
                      star <= Math.round(product.rating || 4.5)
                        ? "fill-[#FF8A00] text-[#FF8A00]"
                        : "fill-slate-200 text-slate-200 dark:fill-slate-700 dark:text-slate-700"
                    )}
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                ))}
              </div>
              <span className="text-[10px] sm:text-xs font-bold text-slate-800 dark:text-slate-200">
                {product.rating > 0 ? product.rating.toFixed(1) : "4.5"}
              </span>
              <span className="text-slate-300 dark:text-slate-600">|</span>
              <span className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 truncate">
                {soldCount}
              </span>
            </div>
          </Link>
        </div>
      </div>
    </article>
  );
}

